import React, { useEffect, useMemo, useRef, useState } from "react";

import UIToggle from "../../Common/UIToggle";
import UIIcon from "../../Common/UIIcon";
import {
   isSubstituteInstructorType,
   getReservationBaseInstructorId,
} from "../../../utils/instructorReservations";
import { filterReservationsAllPages } from "../../../api/reservationsService";
import { getInstructorSubstitutions } from "../../../api/instructorSubstitutionsService";
import {
   readReservationStartAt,
   readReservationId,
} from "../../../utils/normalizeReservation";
import {
   RATING_TREND_MONTHS,
   getCurrentMonthSlot,
   shiftMonthSlot,
   compareMonthSlots,
   buildVisibleMonthWindow,
   monthSlotKey,
   formatMonthTabLabel,
   formatTrendMonthLabel,
   formatStatsPeriodLabel,
} from "./instructorProfileShared";

// Reservation start times are stored as "floating" wall-clock (Chisinau) values,
// so we parse the calendar components directly instead of converting timezones,
// mirroring the calendar's toFloatingDate logic.
function parseFloatingDate(value) {
   if (!value) return null;
   const match = String(value).match(
      /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}))?/,
   );
   if (match) {
      const [, y, mo, d, h = "0", mi = "0"] = match;
      return new Date(+y, +mo - 1, +d, +h, +mi, 0, 0);
   }
   const date = new Date(value);
   return Number.isNaN(date.getTime()) ? null : date;
}

function floatingDateKey(value) {
   const date = parseFloatingDate(value);
   if (!date) return "";
   const mm = String(date.getMonth() + 1).padStart(2, "0");
   const dd = String(date.getDate()).padStart(2, "0");
   return `${date.getFullYear()}-${mm}-${dd}`;
}

function isActiveSubstitution(row) {
   return !(row?.isActive === false || row?.is_active === false);
}

function substitutionBaseId(row) {
   return String(
      row?.baseInstructorId ??
         row?.base_instructor_id ??
         row?.baseInstructor?.id ??
         row?.base_instructor?.id ??
         "",
   ).trim();
}

function substitutionSubstituteId(row) {
   return String(
      row?.substituteInstructorId ??
         row?.substitute_instructor_id ??
         row?.substituteInstructor?.id ??
         row?.substitute_instructor?.id ??
         "",
   ).trim();
}

// Effective instructor of a reservation, reading every shape the backend may
// return: scalar `effectiveInstructorId`, nested `effectiveInstructor`, or an
// active `substitution.substituteInstructorId`. Falls back to the base id.
function readReservationEffectiveInstructorId(row) {
   const nested =
      row?.reservation && typeof row.reservation === "object"
         ? { ...row.reservation, ...row }
         : row || {};

   const scalar =
      nested.effectiveInstructorId ??
      nested.effective_instructor_id ??
      nested.effectiveInstructor?.id ??
      nested.effective_instructor?.id;
   if (scalar !== null && scalar !== undefined && scalar !== "") {
      return String(scalar).trim();
   }

   const substitution =
      nested.substitution ??
      nested.instructorSubstitution ??
      nested.reservation?.substitution ??
      null;
   if (
      substitution &&
      substitution.isActive !== false &&
      substitution.is_active !== false
   ) {
      const substituteId =
         substitution.substituteInstructorId ??
         substitution.substitute_instructor_id ??
         substitution.substituteInstructor?.id ??
         substitution.substitute_instructor?.id;
      if (
         substituteId !== null &&
         substituteId !== undefined &&
         substituteId !== ""
      ) {
         return String(substituteId).trim();
      }
   }

   return String(getReservationBaseInstructorId(row) ?? "").trim();
}

const RESERVATION_PAGE_OPTS = {
   pageSize: 500,
   maxItems: 5000,
   preferSinglePage: true,
   singlePageLimit: 5000,
   singlePageAssumeCompleteIfUnderLimit: true,
};

function isCancelledReservation(row) {
   const nested =
      row?.reservation && typeof row.reservation === "object"
         ? { ...row.reservation, ...row }
         : row || {};
   const flag = nested?.isCancelled ?? nested?.is_cancelled ?? nested?.cancelled;
   if (typeof flag === "boolean") return flag;
   const status = String(nested?.status ?? nested?.state ?? "").toLowerCase();
   return status.includes("cancel") || status.includes("anul");
}

function isWeekendDow(dow) {
   return dow === 0 || dow === 6;
}

function toUtcMonthStart(year, month) {
   return new Date(Date.UTC(year, month, 1, 0, 0, 0, 0)).toISOString();
}

function toUtcMonthEnd(year, month) {
   return new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999)).toISOString();
}

function computeWindowRange(anchor, fullYear) {
   if (fullYear) {
      return {
         startDateFrom: toUtcMonthStart(anchor.year, 0),
         startDateTo: toUtcMonthEnd(anchor.year, 11),
      };
   }

   const firstSlot = shiftMonthSlot(anchor, -(RATING_TREND_MONTHS - 1));
   return {
      startDateFrom: toUtcMonthStart(firstSlot.year, firstSlot.month),
      startDateTo: toUtcMonthEnd(anchor.year, anchor.month),
   };
}

function barHeightPercent(count, maxCount) {
   if (!count || count <= 0) return 0;
   if (!maxCount || maxCount <= 0) return 12;
   return Math.max(14, Math.min(100, Math.round((count / maxCount) * 100)));
}

export default function InstructorLessonsTab({ instructor }) {
   const instructorId = String(instructor?.id ?? "").trim();

   const [anchor, setAnchor] = useState(() => getCurrentMonthSlot());
   const [fullYear, setFullYear] = useState(false);
   const [rows, setRows] = useState([]);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState("");
   const cacheRef = useRef(new Map());

   useEffect(() => {
      setAnchor(getCurrentMonthSlot());
      setFullYear(false);
      cacheRef.current = new Map();
      setRows([]);
      setError("");
   }, [instructorId]);

   const range = useMemo(
      () => computeWindowRange(anchor, fullYear),
      [anchor, fullYear],
   );

   useEffect(() => {
      if (!instructorId) return undefined;

      const cacheKey = `${instructorId}|${range.startDateFrom}|${range.startDateTo}`;
      const cached = cacheRef.current.get(cacheKey);
      if (cached) {
         setRows(cached);
         setLoading(false);
         setError("");
         return undefined;
      }

      let canceled = false;
      setLoading(true);
      setError("");

      (async () => {
         try {
            const dateFrom = floatingDateKey(range.startDateFrom);
            const dateTo = floatingDateKey(range.startDateTo);

            const subs = await getInstructorSubstitutions({
               dateFrom,
               dateTo,
               isActive: true,
            }).catch(() => []);
            if (canceled) return;

            const activeSubs = (Array.isArray(subs) ? subs : []).filter(
               isActiveSubstitution,
            );

            const dedupe = (list) => {
               const byKey = new Map();
               (Array.isArray(list) ? list : []).forEach((row, index) => {
                  const id = readReservationId(row);
                  const key = id
                     ? `id:${id}`
                     : `sig:${floatingDateKey(
                          readReservationStartAt(row),
                       )}|${String(getReservationBaseInstructorId(row) ?? "")}|${index}`;
                  if (!byKey.has(key)) byKey.set(key, row);
               });
               return Array.from(byKey.values());
            };

            let owned = [];

            if (isSubstituteInstructorType(instructor?.instructorType)) {
               // Instructor de schimb: lecțiile lui sunt cele în care el este
               // instructorul efectiv (substituteInstructorId == el). Le obținem
               // pe două căi și le unim, ca să nu depindem de o singură sursă:
               //  1) filtrare directă după effectiveInstructorId
               //  2) rezervările instructorilor de bază din zilele înlocuite lui
               const assignments = new Set();
               const baseIds = new Set();
               for (const sub of activeSubs) {
                  if (substitutionSubstituteId(sub) !== instructorId) continue;
                  const dateKey = floatingDateKey(sub?.date);
                  const baseId = substitutionBaseId(sub);
                  if (!dateKey || !baseId) continue;
                  assignments.add(`${dateKey}|${baseId}`);
                  baseIds.add(baseId);
               }

               const collected = [];

               const byEffective = await filterReservationsAllPages(
                  {
                     effectiveInstructorId: Number(instructorId),
                     startDateFrom: range.startDateFrom,
                     startDateTo: range.startDateTo,
                     sortBy: "startTime",
                     sortOrder: "asc",
                  },
                  RESERVATION_PAGE_OPTS,
               ).catch(() => []);
               if (canceled) return;
               for (const row of Array.isArray(byEffective) ? byEffective : []) {
                  if (readReservationEffectiveInstructorId(row) === instructorId) {
                     collected.push(row);
                  }
               }

               for (const baseId of baseIds) {
                  const baseRows = await filterReservationsAllPages(
                     {
                        instructorId: Number(baseId),
                        startDateFrom: range.startDateFrom,
                        startDateTo: range.startDateTo,
                        sortBy: "startTime",
                        sortOrder: "asc",
                     },
                     RESERVATION_PAGE_OPTS,
                  ).catch(() => []);
                  if (canceled) return;
                  for (const row of Array.isArray(baseRows) ? baseRows : []) {
                     const dateKey = floatingDateKey(readReservationStartAt(row));
                     const rowBaseId = String(
                        getReservationBaseInstructorId(row) ?? "",
                     ).trim();
                     if (assignments.has(`${dateKey}|${rowBaseId}`)) {
                        collected.push(row);
                     }
                  }
               }

               owned = dedupe(collected);
            } else {
               // Instructor de bază: rezervările proprii, dar excludem lecțiile
               // preluate de un înlocuitor (acele lecții nu se calculează la el).
               const baseRows = await filterReservationsAllPages(
                  {
                     instructorId: Number(instructorId),
                     startDateFrom: range.startDateFrom,
                     startDateTo: range.startDateTo,
                     sortBy: "startTime",
                     sortOrder: "asc",
                  },
                  RESERVATION_PAGE_OPTS,
               );
               if (canceled) return;

               const substitutedDays = new Set();
               for (const sub of activeSubs) {
                  if (substitutionBaseId(sub) !== instructorId) continue;
                  const dateKey = floatingDateKey(sub?.date);
                  if (dateKey) substitutedDays.add(dateKey);
               }

               owned = (Array.isArray(baseRows) ? baseRows : []).filter((row) => {
                  const effectiveId = readReservationEffectiveInstructorId(row);
                  if (effectiveId && effectiveId !== instructorId) return false;

                  const dateKey = floatingDateKey(readReservationStartAt(row));
                  return !substitutedDays.has(dateKey);
               });
            }

            cacheRef.current.set(cacheKey, owned);
            setRows(owned);
         } catch (err) {
            if (!canceled) {
               setRows([]);
               setError(err?.message || "Nu am putut încărca lecțiile.");
            }
         } finally {
            if (!canceled) setLoading(false);
         }
      })();

      return () => {
         canceled = true;
      };
   }, [instructor, instructorId, range]);

   const currentMonthSlot = useMemo(() => getCurrentMonthSlot(), []);
   const visibleMonthWindow = useMemo(
      () => buildVisibleMonthWindow(anchor),
      [anchor],
   );
   const canShiftMonthForward =
      compareMonthSlots(anchor, currentMonthSlot) < 0;

   const shiftMonth = (delta) => {
      setFullYear(false);
      setAnchor((current) => {
         const next = shiftMonthSlot(current, delta);
         if (compareMonthSlots(next, getCurrentMonthSlot()) > 0) return current;
         return next;
      });
   };

   const selectMonth = (slot) => {
      setFullYear(false);
      setAnchor(slot);
   };

   const decoratedRows = useMemo(
      () =>
         (Array.isArray(rows) ? rows : [])
            .map((row) => {
               const date = parseFloatingDate(readReservationStartAt(row));
               if (!date) return null;
               return {
                  year: date.getFullYear(),
                  month: date.getMonth(),
                  dow: date.getDay(),
                  cancelled: isCancelledReservation(row),
               };
            })
            .filter(Boolean),
      [rows],
   );

   const periodStats = useMemo(() => {
      const inPeriod = decoratedRows.filter((row) => {
         if (fullYear) return row.year === anchor.year;
         return row.year === anchor.year && row.month === anchor.month;
      });

      const done = inPeriod.filter((row) => !row.cancelled);
      const weekday = done.filter((row) => !isWeekendDow(row.dow)).length;
      const weekend = done.filter((row) => isWeekendDow(row.dow)).length;
      const cancelled = inPeriod.filter((row) => row.cancelled).length;

      return {
         total: done.length,
         weekday,
         weekend,
         cancelled,
      };
   }, [anchor.month, anchor.year, decoratedRows, fullYear]);

   const trendBuckets = useMemo(() => {
      const slots = Array.from({ length: RATING_TREND_MONTHS }, (_, index) =>
         shiftMonthSlot(anchor, index - (RATING_TREND_MONTHS - 1)),
      );

      const counts = new Map();
      for (const row of decoratedRows) {
         if (row.cancelled) continue;
         const key = `${row.year}-${row.month}`;
         counts.set(key, (counts.get(key) || 0) + 1);
      }

      const buckets = slots.map((slot) => {
         const isFuture = compareMonthSlots(slot, currentMonthSlot) > 0;
         const count = isFuture ? 0 : counts.get(`${slot.year}-${slot.month}`) || 0;
         return {
            key: monthSlotKey(slot),
            label: formatTrendMonthLabel(slot.month, slot.year),
            count,
            isFuture,
            isSelected:
               !fullYear &&
               slot.year === anchor.year &&
               slot.month === anchor.month,
         };
      });

      const maxCount = buckets.reduce(
         (max, bucket) => (bucket.isFuture ? max : Math.max(max, bucket.count)),
         0,
      );

      return { buckets, maxCount };
   }, [anchor, currentMonthSlot, decoratedRows, fullYear]);

   const periodLabel = formatStatsPeriodLabel({
      year: anchor.year,
      month: anchor.month,
      fullYear,
   });

   const trendPeriodKey = fullYear
      ? `year-${anchor.year}`
      : `month-${anchor.year}-${anchor.month}`;

   const axisMax = trendBuckets.maxCount;
   const axisMid = Math.round(axisMax / 2);

   return (
      <div className="instructorProfileUI__section instructorProfileUI__section--stats">
         <div className="instructorProfileUI__statsMonthRow">
            <button
               type="button"
               className="instructorProfileUI__tab instructorProfileUI__statsMonthNav"
               onClick={() => shiftMonth(-1)}
               disabled={loading}
               aria-label="Luna anterioară"
            >
               <UIIcon
                  name="arrow"
                  className="instructorProfileUI__statsMonthNavIcon"
               />
            </button>

            <div className="instructorProfileUI__statsTabShell">
               <div
                  className="instructorProfileUI__statsMonthTabs"
                  role="tablist"
                  aria-label="Luni"
               >
                  {visibleMonthWindow.map((slot) => {
                     const isActive =
                        !fullYear &&
                        slot.year === anchor.year &&
                        slot.month === anchor.month;
                     const isCurrent =
                        slot.year === currentMonthSlot.year &&
                        slot.month === currentMonthSlot.month;

                     return (
                        <button
                           key={monthSlotKey(slot)}
                           type="button"
                           role="tab"
                           aria-selected={isActive}
                           className={`instructorProfileUI__tab instructorProfileUI__statsMonthTab ${
                              isActive ? "is-active" : ""
                           } ${isCurrent ? "is-current" : ""}`}
                           onClick={() => selectMonth(slot)}
                           disabled={loading}
                        >
                           {formatMonthTabLabel(slot)}
                        </button>
                     );
                  })}
               </div>
            </div>

            <button
               type="button"
               className="instructorProfileUI__tab instructorProfileUI__statsMonthNav"
               onClick={() => shiftMonth(1)}
               disabled={loading || !canShiftMonthForward}
               aria-label="Luna următoare"
            >
               <UIIcon
                  name="arrow"
                  className="instructorProfileUI__statsMonthNavIcon instructorProfileUI__statsMonthNavIcon--next"
               />
            </button>
         </div>

         <div className="instructorProfileUI__statsYearRow">
            <span className="instructorProfileUI__fieldLabel">
               Tot anul {anchor.year}
            </span>
            <UIToggle
               checked={fullYear}
               onChange={(nextValue) => setFullYear(nextValue)}
               disabled={loading}
               aria-label={`Afișează tot anul ${anchor.year}`}
            />
         </div>

         {loading && !rows.length && (
            <p className="instructorProfileUI__status">Se încarcă lecțiile...</p>
         )}

         {!loading && error && (
            <p className="instructorProfileUI__error">{error}</p>
         )}

         {!error && (rows.length || !loading) && (
            <>
               <div className="instructorProfileUI__statsSummary">
                  <div className="instructorProfileUI__statsCard instructorProfileUI__statsCard--average">
                     <span className="instructorProfileUI__fieldLabel">
                        Lecții · {periodLabel}
                     </span>
                     <span className="instructorProfileUI__statsValue instructorProfileUI__statsValue--average">
                        {periodStats.total}
                     </span>
                  </div>

                  <div className="instructorProfileUI__statsCard">
                     <span className="instructorProfileUI__fieldLabel">
                        În săptămână
                     </span>
                     <span className="instructorProfileUI__statsValue">
                        {periodStats.weekday}
                     </span>
                  </div>

                  <div className="instructorProfileUI__statsCard">
                     <span className="instructorProfileUI__fieldLabel">
                        Weekend
                     </span>
                     <span className="instructorProfileUI__statsValue">
                        {periodStats.weekend}
                     </span>
                  </div>

                  <div className="instructorProfileUI__statsCard">
                     <span className="instructorProfileUI__fieldLabel">
                        Anulate
                     </span>
                     <span className="instructorProfileUI__statsValue">
                        {periodStats.cancelled}
                     </span>
                  </div>
               </div>

               <div className="instructorProfileUI__statsTrendPanel">
                  <div className="instructorProfileUI__statsTrendHead">
                     <h3 className="instructorProfileUI__statsTitle">
                        Evoluție lecții
                     </h3>
                  </div>

                  <div className="instructorProfileUI__statsTrendChartWrap instructorProfileUI__statsTrendChartWrap--lessons">
                     <div className="instructorProfileUI__statsTrendYAxis">
                        <span>{axisMax}</span>
                        <span>{axisMid}</span>
                        <span>0</span>
                     </div>

                     <div className="instructorProfileUI__statsTrendPlot">
                        <div className="instructorProfileUI__statsTrendBarRow">
                           {trendBuckets.buckets.map((bucket, index) => {
                              const hasLessons = bucket.count > 0;
                              return (
                                 <div
                                    key={bucket.key}
                                    className="instructorProfileUI__statsTrendBarArea instructorProfileUI__statsTrendBarArea--lessons"
                                 >
                                    <div
                                       key={`${bucket.key}-${trendPeriodKey}`}
                                       className={`instructorProfileUI__statsTrendBar instructorProfileUI__statsTrendBar--lessons ${
                                          hasLessons ? "is-rated" : "is-empty"
                                       }`}
                                       style={
                                          hasLessons
                                             ? {
                                                  "--stats-trend-bar-height": `${barHeightPercent(
                                                     bucket.count,
                                                     trendBuckets.maxCount,
                                                  )}%`,
                                                  animationDelay: `${
                                                     index * 0.07
                                                  }s`,
                                               }
                                             : undefined
                                       }
                                    >
                                       {hasLessons ? (
                                          <span className="instructorProfileUI__lessonsTrendCount">
                                             {bucket.count}
                                          </span>
                                       ) : null}
                                    </div>
                                 </div>
                              );
                           })}
                        </div>

                        <div className="instructorProfileUI__statsTrendLabelRow">
                           {trendBuckets.buckets.map((bucket) => (
                              <span
                                 key={`${bucket.key}-label`}
                                 className={`instructorProfileUI__statsTrendLabel ${
                                    bucket.isSelected ? "is-current" : ""
                                 }`}
                              >
                                 {bucket.label}
                              </span>
                           ))}
                        </div>
                     </div>
                  </div>
               </div>
            </>
         )}
      </div>
   );
}
